import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ServiceOrderPhoto } from '@/types/service';
import { useToast } from '@/hooks/use-toast';

async function compressImage(file: File, maxWidthPx = 1200): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxWidthPx / img.width);
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(img.width  * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);

      const qualities = [0.82, 0.7, 0.55];
      const attempt = (index: number) => {
        canvas.toBlob(
          blob => {
            if (!blob) {
              reject(new Error('Falha na compressão'));
              return;
            }
            if (blob.size <= 900_000 || index >= qualities.length - 1) {
              if (blob.size > 900_000) {
                reject(new Error('Foto excede o tamanho máximo mesmo após compressão'));
                return;
              }
              resolve(blob);
              return;
            }
            attempt(index + 1);
          },
          'image/jpeg',
          qualities[index],
        );
      };
      attempt(0);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Imagem inválida'));
    };
    img.src = url;
  });
}

export function useServiceOrderPhotos(serviceOrderId: string | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);

  const query = useQuery<ServiceOrderPhoto[]>({
    queryKey: ['service-order-photos', serviceOrderId],
    queryFn: async () => {
      if (!serviceOrderId) return [];
      const { data, error } = await supabase
        .from('service_order_photos')
        .select('id, service_order_id, storage_path, caption, position, uploaded_at')
        .eq('service_order_id', serviceOrderId)
        .order('position', { ascending: true });
      if (error) throw error;

      const photos = await Promise.all(
        (data ?? []).map(async (photo) => {
          const { data: urlData } = await supabase.storage
            .from('service-order-images')
            .createSignedUrl((photo as ServiceOrderPhoto).storage_path, 3600);
          return {
            ...(photo as ServiceOrderPhoto),
            publicUrl: urlData?.signedUrl ?? undefined,
          };
        }),
      );
      return photos;
    },
    enabled: !!serviceOrderId,
  });

  const uploadPhoto = useMutation({
    mutationFn: async ({
      file,
      caption,
      serviceOrderId: soId,
    }: {
      file: File;
      caption?: string;
      serviceOrderId: string;
    }) => {
      setUploading(true);
      try {
        const compressed = await compressImage(file);
        const path = `${soId}/${Date.now()}.jpg`;
        const { error: storageError } = await supabase.storage
          .from('service-order-images')
          .upload(path, compressed, { contentType: 'image/jpeg', upsert: false });
        if (storageError) throw storageError;

        const currentPhotos =
          queryClient.getQueryData<ServiceOrderPhoto[]>([
            'service-order-photos', soId,
          ]) ?? [];
        const nextPosition = currentPhotos.length;

        const { error: dbError } = await supabase
          .from('service_order_photos')
          .insert({
            service_order_id: soId,
            storage_path: path,
            caption: caption ?? null,
            position: nextPosition,
          });
        if (dbError) throw dbError;
      } finally {
        setUploading(false);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['service-order-photos', variables.serviceOrderId],
      });
    },
    onError: (err: Error) => {
      toast({
        title: 'Erro ao enviar foto',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  const deletePhoto = useMutation({
    mutationFn: async ({
      photo,
      soId,
    }: {
      photo: ServiceOrderPhoto;
      soId: string;
    }) => {
      await supabase.storage
        .from('service-order-images')
        .remove([photo.storage_path]);
      const { error } = await supabase
        .from('service_order_photos')
        .delete()
        .eq('id', photo.id);
      if (error) throw error;
      void soId;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['service-order-photos', variables.soId],
      });
    },
    onError: (err: Error) => {
      toast({
        title: 'Erro ao remover foto',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  return { ...query, uploading, uploadPhoto, deletePhoto };
}
